import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ApiEndpoints } from '../../../../constants/endpoints/apiEndpoints.ts';
import useHookHub from '../../../../util/hubs/useHookHub';
import { ActivityOpenNotificationContext } from './ActivityOpenNotificationContext';

type ActivityOpenNotificationServerRpc = {
  OnCloseActivity: (activityId: number) => void;
  OnOpenActivity: (activityId: number) => string | undefined;
};

export default function ActivityOpenNotificationProvider(props: PropsWithChildren) {
  const [openBy, setOpenBy] = useState<string | undefined>(undefined);

  const [invoke] = useHookHub<ActivityOpenNotificationServerRpc>(ApiEndpoints.hubs.activityNotificationHub, {});
  // Activities capture these callbacks on mount, often before the hub connects, so read the latest invoke from a ref
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  // An activity opened before the hub connects is announced once the connection is ready
  const pendingActivityId = useRef<number | undefined>(undefined);

  const announceOpen = (activityId: number) => {
    invokeRef.current?.('OnOpenActivity', activityId).then((val) => {
      setOpenBy(val);
    });
  };

  useEffect(() => {
    if (invoke && pendingActivityId.current !== undefined) {
      announceOpen(pendingActivityId.current);
      pendingActivityId.current = undefined;
    }
  }, [invoke]);

  const onOpenActivity = (activityId: number) => {
    if (!invokeRef.current) {
      pendingActivityId.current = activityId;
      return;
    }

    announceOpen(activityId);
  };

  const onCloseActivity = async (activityId: number) => {
    if (pendingActivityId.current === activityId) {
      pendingActivityId.current = undefined;
    }

    if (invokeRef.current) {
      await invokeRef.current('OnCloseActivity', activityId);
    }
  };
  return (
    <ActivityOpenNotificationContext.Provider value={{ onOpenActivity, onCloseActivity, openBy }}>
      {props.children}
    </ActivityOpenNotificationContext.Provider>
  );
}
